/**
 * Workshop A2 (Plan 2026-10-29-workshop-luecken, Paket P): „Was eine Anmeldung wert ist". Je beworbenem Produkt der
 * Erwartete Wert (bei Bestellung, netto) aller zugeordneten Anfos je Live-Anmeldung, dazu als Marke der vertretbare
 * Einkaufspreis (CPA) je Anmeldung. Dieselbe Population wie die Webinar-Tabelle (Till 29.10., Q5); der Anteil der Anfos
 * ohne Anmeldung steht daneben. Nur fertige Termine (≥ 14 Tage), grau unter 20 Anfos. Der Anfo-Wert ist eine Vorhersage,
 * die noch nicht nachgeprüft ist — die Karte sagt das sichtbar. Backend `services/auswertungen/wertJeAnmeldungService.ts`.
 */
import { FC, useMemo } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { BefundZeile, nachGruppenReihenfolge, tEuro } from '../../../components/auswertungen/kennzahlTeile';
import { Legende, TipRow, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { formatEur, formatPct, spaltenTip, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchWertJeAnmeldung, WertJeAnmeldungZeile } from '../../../helpers/auswertungenData';
import { balkenHoehe, Hinweis, int, Laden, ProduktZelle, Strich, Unter, useLaden } from './shared';
import { wertJeAnmeldungSatz } from './befunde';

const GRUPPEN = ['Wert', 'Menge'] as const;
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['produkt', 'wert', 'budget', 'anm', 'anfos', 'ohne'],
  mittel: ['produkt', 'wert', 'budget', 'anm', 'anfos'],
  schmal: ['produkt', 'wert', 'budget'],
};

const name = (r: WertJeAnmeldungZeile): string => (r.plx_product ? r.plx_product : '(ohne Produkt)');

/** Befund (rein): höchster und niedrigster Wert je Anmeldung unter den nicht grauen Produkten. */
// Audit C3: der Satz lebt in `befunde.ts` (erste Nennung „Name (Kürzel)").
export function wertJeAnmeldungBefund(rows: WertJeAnmeldungZeile[]): string | null {
  return wertJeAnmeldungSatz(rows, (v) => formatEur(v, 2));
}

const WertJeAnmeldungKarte: FC = () => {
  const { data, fehler } = useLaden(fetchWertJeAnmeldung);
  const viz = useVizStyle();
  const breit = useMediaQuery(useTheme().breakpoints.up('md'));

  const chartData = useMemo(
    () =>
      (data?.rows ?? [])
        .filter((r) => !r.grau && r.wert_je_anmeldung !== null)
        .map((r) => ({ produkt: r.plx_product || 'ohne Produkt', name: r.name, wert: r.wert_je_anmeldung as number, budget: r.budget_je_anmeldung, anm: r.registrations, anfos: r.anfos_fest }))
        .sort((a, b) => a.wert - b.wert),
    [data],
  );
  const s = data?.summary;
  const prozent = data?.cpa_prozent;

  const baue = (breitSpalte = true): Spalte<WertJeAnmeldungZeile>[] => [
    {
      key: 'produkt',
      label: 'Produkt',
      tip: 'Beworbenes Produkt der Termine (wie in der Webinar-Tabelle).',
      gruppe: 'Wert',
      pflicht: true,
      minWidth: breitSpalte ? 140 : undefined,
      // Schmal: Produktnamen dürfen silbengetrennt umbrechen — „TopStockTrader“ hielt die Spalte sonst so breit, dass die
      // Tabelle bei 390 px quer scrollte (Abnahme 29.10.).
      render: (r) =>
        breitSpalte ? (
          <ProduktZelle code={name(r)} name={r.name} />
        ) : (
          <Box lang="de" sx={{ hyphens: 'auto', overflowWrap: 'anywhere' }}>
            <ProduktZelle code={name(r)} name={r.name} />
          </Box>
        ),
      sortValue: (r) => name(r),
    },
    {
      key: 'wert',
      label: TERMS.wertJeAnmeldung.label,
      einheit: 'netto · Summe',
      tip: `${TERMS.wertJeAnmeldung.tip} ${TERMS.vorhersageUngeprueft.tip}`,
      gruppe: 'Wert',
      pflicht: true,
      align: 'right',
      unsicher: (r) => (r.grau ? `unter ${data?.min_anfos ?? 20} Anfos` : null),
      render: (r) =>
        r.wert_je_anmeldung === null ? (
          <Strich grund="Keine Live-Anmeldung." />
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatEur(r.wert_je_anmeldung, 2)}
            <Unter>{tEuro(r.wert_summe_fest)} Erwarteter Wert</Unter>
          </Box>
        ),
      sortValue: (r) => r.wert_je_anmeldung,
      summary: s ? (
        <Box sx={{ lineHeight: 1.25 }}>
          {formatEur(s.wert_je_anmeldung, 2)}
          <Unter>{tEuro(s.wert_summe_fest)} Erwarteter Wert</Unter>
        </Box>
      ) : null,
    },
    {
      key: 'budget',
      label: TERMS.budgetJeAnmeldung.label,
      einheit: prozent != null ? `${formatPct(prozent, 0)} des Werts` : 'netto',
      tip: TERMS.budgetJeAnmeldung.tip,
      gruppe: 'Wert',
      align: 'right',
      unsicher: (r) => (r.grau ? `unter ${data?.min_anfos ?? 20} Anfos` : null),
      render: (r) => (r.budget_je_anmeldung === null ? <Strich grund="Keine Live-Anmeldung." /> : formatEur(r.budget_je_anmeldung, 2)),
      sortValue: (r) => r.budget_je_anmeldung,
      summary: s ? formatEur(s.budget_je_anmeldung, 2) : null,
    },
    {
      key: 'anm',
      label: TERMS.anmeldungenLive.label,
      einheit: 'live',
      tip: TERMS.anmeldungenLive.tip,
      gruppe: 'Menge',
      align: 'right',
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          {int(r.registrations)}
          <Unter>
            {int(r.webinare)} {r.webinare === 1 ? 'Termin' : 'Termine'}
          </Unter>
        </Box>
      ),
      sortValue: (r) => r.registrations,
      summary: s ? (
        <Box sx={{ lineHeight: 1.25 }}>
          {int(s.registrations)}
          <Unter>{int(s.webinare)} Termine</Unter>
        </Box>
      ) : null,
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      einheit: 'zugeordnet, ohne vorläufige',
      tip: spaltenTip('anfos', `Alle Anfos, die den Terminen zugeordnet sind, ohne vorläufig ergänzte.`),
      gruppe: 'Menge',
      align: 'right',
      render: (r) => int(r.anfos_fest),
      sortValue: (r) => r.anfos_fest,
      summary: int(s?.anfos_fest),
    },
    {
      key: 'ohne',
      label: TERMS.anfosOhneAnmeldung.label,
      einheit: 'Anteil',
      tip: TERMS.anfosOhneAnmeldung.tip,
      gruppe: 'Menge',
      align: 'right',
      render: (r) =>
        r.anteil_ohne_anmeldung === null ? (
          '–'
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatPct(r.anteil_ohne_anmeldung, 0)}
            <Unter>
              {int(r.anfos_ohne_anmeldung)} von {int(r.anfos_fest)}
            </Unter>
          </Box>
        ),
      sortValue: (r) => r.anteil_ohne_anmeldung,
      summary: s ? (
        <Box sx={{ lineHeight: 1.25 }}>
          {formatPct(s.anteil_ohne_anmeldung, 0)}
          <Unter>
            {int(s.anfos_ohne_anmeldung)} von {int(s.anfos_fest)}
          </Unter>
        </Box>
      ) : null,
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue()), GRUPPEN);
  const wahl = useSpaltenwahl('wertJeAnmeldung', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(wahl.breite === 'breit').filter((c) => sichtbar.has(c.key));
  const befund = data ? wertJeAnmeldungBefund(data.rows) : null;
  const maxWert = Math.max(0, ...chartData.map((d) => Math.max(d.wert, d.budget ?? 0)));
  // Linker Rand nach dem längsten Achsen-Etikett: „(ohne Produkt)“ war bei festen 72 px links abgeschnitten (Browser-Abnahme 29.10.).
  const randLinks = Math.max(56, Math.round(6.5 * Math.max(0, ...chartData.map((d) => d.produkt.length))) + 14);

  return (
    <ChartCard
      title="Was eine Anmeldung wert ist"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead="Je Produkt: Welchen Erwarteten Wert bringt eine Live-Anmeldung zu einem Webinar, und wie viel darf eine Anmeldung im Einkauf höchstens kosten (Strich)? Gezählt werden alle Anfos, die den Terminen zugeordnet sind, auch ohne Anmeldung — wie in der Webinar-Tabelle. Der Erwartete Wert ist eine Vorhersage, die noch nicht nachgeprüft ist."
      terms={[
        { label: TERMS.wertJeAnmeldung.label, tip: TERMS.wertJeAnmeldung.tip },
        { label: TERMS.budgetJeAnmeldung.label, tip: TERMS.budgetJeAnmeldung.tip },
        { label: TERMS.anfosOhneAnmeldung.label, tip: TERMS.anfosOhneAnmeldung.tip },
        { label: TERMS.anmeldungenLive.label, tip: TERMS.anmeldungenLive.tip },
        { label: TERMS.vorhersageUngeprueft.label, tip: TERMS.vorhersageUngeprueft.tip },
      ]}
      source="Webinar-Anmeldungen, PL-X-Bestellungen, Zuordnung zu Webinar-Terminen, Anfo-Wert und CPA je Anfo"
      freshnessKey={['plx_orders', 'webinar_zuordnung', 'anfo_werte']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            <BefundZeile
              text={befund}
              fehlt={befund ? null : `Zu wenige Produkte mit mindestens ${data.min_anfos} Anfos.`}
              tip={`${TERMS.wertJeAnmeldung.tip} ${TERMS.budgetJeAnmeldung.tip}`}
            />
            <Box sx={{ fontSize: 13, mb: 1.5, px: 1.5, py: 1, borderRadius: '8px', border: 1, borderColor: 'warning.light', maxWidth: '75ch', lineHeight: 1.5 }}>
              <strong>{TERMS.vorhersageUngeprueft.label}:</strong> Der Erwartete Wert kommt aus dem Anfo-Wert-Modell. Ob er mit dem später
              gezahlten Geld übereinstimmt, ist noch nicht geprüft. Die Euro-Werte sind Richtwerte, keine Zusage.
            </Box>
            <Legende
              items={[
                { label: TERMS.wertJeAnmeldung.short, color: viz.series1 },
                { label: `${TERMS.budgetJeAnmeldung.label}${prozent != null ? ` (${formatPct(prozent, 0)} des Werts)` : ''}`, color: viz.inkSecondary, shape: 'line' as const },
              ]}
            />
            <Box sx={{ height: balkenHoehe(chartData.length) }}>
              <ResponsiveBar
                data={chartData as unknown as BarDatum[]}
                keys={['wert']}
                indexBy="produkt"
                layout="horizontal"
                margin={{ top: 4, right: 56, bottom: 36, left: randLinks }}
                padding={0.3}
                borderRadius={4}
                maxValue={maxWert > 0 ? maxWert * 1.05 : 'auto'}
                colors={[viz.series1]}
                theme={viz.nivo}
                animate={false}
                enableGridY={false}
                enableGridX
                gridXValues={breit ? 5 : 3}
                enableLabel={false}
                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: breit ? 5 : 3, format: (v) => `${Number(v).toLocaleString('de-DE')} €` }}
                layers={[
                  'grid',
                  'axes',
                  'bars',
                  // Budget-Marke: senkrechter Strich je Balken beim CPA je Anmeldung.
                  ({ bars, xScale }) => (
                    <g>
                      {bars
                        .filter((b) => b.data.data.budget != null)
                        .map((b) => {
                          const x = (xScale as (v: number) => number)(Number(b.data.data.budget));
                          return <line key={`budget-${b.key}`} x1={x} x2={x} y1={b.y - 3} y2={b.y + b.height + 3} stroke={viz.inkSecondary} strokeWidth={2.5} />;
                        })}
                    </g>
                  ),
                  ({ bars }) => (
                    <g>
                      {bars.map((b) => (
                        <text key={b.key} x={b.x + b.width + 6} y={b.y + b.height / 2} dominantBaseline="central" fontSize={12} fontFamily={viz.fontFamily} fill={viz.inkSecondary}>
                          {formatEur(Number(b.data.value), 2)}
                        </text>
                      ))}
                    </g>
                  ),
                ]}
                tooltip={({ data: d }) => (
                  <Box>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                      {String(d.produkt)}
                      {d.name && d.name !== d.produkt && d.name !== '(ohne Produkt)' ? ` · ${String(d.name)}` : ''}
                    </Box>
                    <TipRow color={viz.series1} label={TERMS.wertJeAnmeldung.short} value={formatEur(Number(d.wert), 2)} />
                    <TipRow label={TERMS.budgetJeAnmeldung.label} value={formatEur(d.budget == null ? null : Number(d.budget), 2)} />
                    <TipRow label="Live-Anmeldungen" value={int(Number(d.anm))} />
                    <TipRow label="Anfos" value={int(Number(d.anfos))} />
                  </Box>
                )}
                role="img"
                ariaLabel="Erwarteter Wert je Live-Anmeldung je Produkt, mit dem vertretbaren Einkaufspreis je Anmeldung als Strich"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <SortableTable
                rows={data.rows}
                columns={spalten}
                rowKey={(r) => r.plx_product || '(ohne)'}
                initialSort={{ key: 'wert', dir: 'desc' }}
                maxHeight={null}
                eng={wahl.breite !== 'breit'}
                muted={(r) => r.grau}
                mutedLast
                summaryLabel="Alle Termine"
              />
            </Box>
            <Hinweis>
              Euro netto, Wert bei Bestellung ({data.anfo_wert_stand}). Termine von {data.window.von.split('-').reverse().join('.')} bis zum Stichtag, die
              mindestens {data.window.reife_tage} Tage alt sind ({int(data.webinare_jung)} jüngere fehlen); gezählt beim beworbenen Produkt. Grau = unter{' '}
              {data.min_anfos} Anfos; solche Produkte fehlen in der Grafik.
              {prozent != null && ` Der Einkaufspreis ist ${formatPct(prozent, 0)} des Erwarteten Werts (Einstellung des Anfo-Wert-Modells).`}
              {s && s.with_cpa_fest < s.anfos_fest && ` ${int(s.anfos_fest - s.with_cpa_fest)} Anfos ohne Wert zählen 0 €.`}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default WertJeAnmeldungKarte;
